import React, { useEffect, useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import styled from 'styled-components';
import { animateScroll as scroll } from 'react-scroll';

import { State } from 'src/state/ducks';
import * as bakedBreadActions from 'src/state/ducks/bakedBread/actions';
import * as bakedBreadTagActions from 'src/state/ducks/bakedBreadTag/actions';
import * as notificationTemplateActions from 'src/state/ducks/notificationTemplate/actions';
import * as shopActions from 'src/state/ducks/shop/actions';
import { NotificationTemplateEntity } from 'src/firebase/db/NotificationTemplates';

import StyleConfig from 'src/constants/StyleConfig';
import { DefaultPageProps, HistoryProps } from 'src/constants/Interface';

import { DefaultLayout } from 'src/components/layouts/DefaultLayout';
import Heading from 'src/components/atoms/typo/Heading';
import NotificationTemplateCreateForm from 'src/components/organisms/form/NotificationTemplateCreateForm';
import NotificationTemplateConfirmForm from 'src/components/organisms/form/NotificationTemplateConfirmForm';
import SpinnerModal from 'src/components/organisms/loading/SpinnerModal';

type Props = DefaultPageProps &
  HistoryProps &
  ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

const NotificationTemplateCreatePage = (props: Props) => {
  const [selectedTagId, setSelectedTagId] = useState('');

  const {
    login_shop_id,
    getShop,
    getBakedBreads,
    getBakedBreadTags,
    draft,
    bakedBreads,
    bakedBreadTags,
    shop,
    isFetching,
    isValidated,
    init,
    validate,
    edit,
    create,
    errors,
  } = props;

  useEffect(() => {
    if (!isValidated || errors.length) {
      scroll.scrollToTop();
    } else {
      window.scrollTo(0, 0);
    }
  }, [errors, isValidated]);

  useEffect(() => {
    const fetch = async () => {
      await init();
      await getShop(login_shop_id);
      await getBakedBreads(login_shop_id);
      await getBakedBreadTags();
    };
    fetch();
    return () => {
      init();
    };
  }, [getBakedBreads, getBakedBreadTags, getShop, init, login_shop_id]);

  const onEdit = useCallback(async () => {
    scroll.scrollToTop();
    await edit(draft);
  }, [edit, draft]);

  const oncCreate = useCallback(async () => {
    await create(draft);
  }, [create, draft]);

  const onValidate = useCallback(
    (payload: NotificationTemplateEntity) => {
      validate(payload);
    },
    [validate],
  );

  const onChangeTagFilter = useCallback((bakedBreadTagId: string) => {
    setSelectedTagId(bakedBreadTagId);
  }, []);

  return (
    <DefaultLayout {...props}>
      <Container>
        <SpinnerModal isLoading={isFetching} />

        {isValidated ? (
          <>
            <Heading tag="h1" text="テンプレート内容のご確認" />
            <Lead>下記の内容でテンプレートを新規登録しますか？</Lead>
            <NotificationTemplateConfirmForm
              notificationTemplate={draft}
              bakedBreads={bakedBreads}
              onBack={onEdit}
              onSubmit={oncCreate}
            />
          </>
        ) : (
          <>
            <Heading tag="h1" text="通知用のテンプレートを新規登録します。" />
            <NotificationTemplateCreateForm
              bakedBreads={bakedBreads}
              bakedBreadTags={bakedBreadTags}
              selectedTagId={selectedTagId}
              shop={shop}
              onChangeTag={onChangeTagFilter}
              onSubmit={onValidate}
              errors={errors}
              draft={draft}
            />
          </>
        )}
      </Container>
    </DefaultLayout>
  );
};

const Container = styled.div`
  box-sizing: border-box;
  padding: ${StyleConfig.HELPER.M16};
  .order__annotaion {
    font-size: 12px;
    color: ${StyleConfig.COLOR.SECONDORY};
    &.bold {
      font-size: 14px;
      font-weight: bold;
      padding: ${StyleConfig.HELPER.M16};
    }
  }
`;
const Lead = styled.p`
  font-size: 14px;
  line-height: ${24 / 14};
  font-weight: bold;
`;

const mapStateToProps = (state: State) => {
  const {
    notificationTemplate: {
      template: notificationTemplate,
      isValidated,
      validationErrors: errors,
      draft,
    },
    bakedBread: { resource: bakedBreads },
    shop: {
      currentShop: { resource: shop },
    },
    bakedBreadTag: { resource: bakedBreadTags },
  } = state;

  return {
    draft,
    notificationTemplate,
    isFetchedNotificationTemplate: state.notificationTemplate.isFetched,
    bakedBreads: bakedBreads.filter((v) => !v.is_stopped),
    bakedBreadTags: bakedBreadTags.filter(
      ({ is_disabled, is_featured }) => !is_disabled && is_featured,
    ),
    shop,
    isFetching: state.bakedBread.isFetching || state.shop.isFetching,
    isValidated,
    errors,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  getShop: (shop_id: string) => dispatch(shopActions.getShop(shop_id)),
  getBakedBreads: (shop_id: string) => dispatch(bakedBreadActions.getBakedBreads(shop_id)),
  getBakedBreadTags: () => dispatch(bakedBreadTagActions.getBakedBreadTags()),
  init: () => dispatch(notificationTemplateActions.initNotificationTemplate()),
  validate: (payload: NotificationTemplateEntity) =>
    dispatch(notificationTemplateActions.validateNotificationTemplate(payload)),
  edit: (payload: NotificationTemplateEntity) =>
    dispatch(notificationTemplateActions.editNotificationTemplate(payload)),
  create: async (payload: NotificationTemplateEntity) =>
    dispatch(notificationTemplateActions.createNotificationTemplate(payload)),
});
export default connect(mapStateToProps, mapDispatchToProps)(NotificationTemplateCreatePage);
